import React, { useMemo, useState } from 'react';
import {
  Box, Grid, Typography, Paper, Stack, Autocomplete, TextField, Button, Alert,
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import TaxCalculatorForm from '../../taxCalculator/TaxCalculatorForm';
import TaxSlabsPanel from '../../taxCalculator/TaxSlabsPanel';
import TaxCalculatorInfo from '../../taxCalculator/TaxCalculatorInfo';
import { useAllEmployeeDetails } from '../../../hooks/useEmployees';

// ---- FORTHEYE soft-card tokens (shared with the employee portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';
const OUTLINED_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' };

export default function TaxCalculatorAdmin() {
  const { data: employees = [], isLoading } = useAllEmployeeDetails();
  const [picked, setPicked] = useState(null);

  const options = useMemo(
    () =>
      employees
        .filter((e) => e?.employee_no && e?.name)
        .map((e) => ({
          label: `${e.name} (${e.employee_no})`,
          employee_no: e.employee_no,
          name: e.name,
        })),
    [employees]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <CalculateIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
              Income Tax Calculator
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Pick an employee to auto-load their salary master and run a what-if tax calculation for
              AY 2027-28 / 2026-27. Use <strong>Clear</strong> on the form to reset inputs.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Employee picker card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <Autocomplete
            sx={{ flex: 1, minWidth: 280, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            options={options}
            loading={isLoading}
            value={picked}
            onChange={(_, val) => setPicked(val)}
            renderInput={(params) => (
              <TextField {...params} label="Select Employee" placeholder="Search by name or employee no" />
            )}
            isOptionEqualToValue={(o, v) => o.employee_no === v.employee_no}
          />
          {picked && (
            <Button variant="outlined" onClick={() => setPicked(null)} sx={OUTLINED_BTN}>
              Change Employee
            </Button>
          )}
        </Stack>
      </Paper>

      {!picked && (
        <Alert severity="info" sx={{ borderRadius: '14px', mb: { xs: 2, md: 3 } }}>Select an employee above to begin.</Alert>
      )}

      {picked && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <TaxCalculatorForm
              employeeNo={picked.employee_no}
              employeeName={picked.name}
              onClear={() => { /* keep employee selected; form already reset */ }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TaxSlabsPanel />
          </Grid>
        </Grid>
      )}

      <Box sx={{ mt: 3 }}>
        <TaxCalculatorInfo />
      </Box>
    </Box>
  );
}
